/**
 * A Suggestion Engine strategy card in the aged care anatomy — the shared card for the modules
 * whose baseline is "don't do it" (downsizer, granny flat). The village keeps its own card with the
 * same anatomy (RvStrategyCard); this one takes the module's words as props.
 *
 * Top to bottom (owner, 24 Sep 2026): what the plan is, WHAT IT DOES (the money flow and the plan
 * in one sentence), the figures with the baseline under each, then the three buttons (owner, 2 Oct
 * 2026: Explain this / What could go wrong / Select — the year-by-year lives inside Explain this).
 *
 * THE RAIL HOLDS THE BASELINE in engine mode, so this card is the only place a plan is described:
 * every figure under a tile says what the baseline gives and how far this plan moves it. Nothing
 * here is re-derived — the strategy's own snapshot and the baseline candidate's, quoted.
 *
 * WORTH KNOWING, ON THE CARD (owner, 24 Sep 2026: "you're going to get me sued if a client pleads
 * ignorance"): a nil-residual annuity or an age-and-health warning is said in amber on the card
 * itself, never only inside "Explain this".
 */
import React from 'react';
import { BookOpen, CheckCircle, ShieldAlert } from 'lucide-react';
import type { AlternativeAssetNote } from '../../strategy/alternativeAssetNote.ts';
import { Badge, Button } from '../ui/index.tsx';
import { PlainWordsCard, MoneyFlow, type MoneyFlowStep } from '../narrative/PlainWords.tsx';
import { formatCurrency } from '../../utils/index.ts';

export interface EngineCardSnapshot {
    pension?: number;
    netCashflow?: number;
    legacyValue?: number;
}

export interface EngineStrategyCardProps {
    name: string;
    description?: string;
    badges: string[];
    belowBaseline?: string;
    snapshot: EngineCardSnapshot;
    /** The baseline candidate's snapshot — pension, cashflow and estate every tile is measured against. */
    baseline: EngineCardSnapshot | null | undefined;
    /** "not downsizing", "not entering" — the words under each tile. */
    baselineWord: string;
    legacyYears: number;
    /** The plan's money flow (home sold → new home …), drawn above the sentence. */
    flow: MoneyFlowStep[];
    /** The plan in one sentence — the module's in-brief, measured against the baseline. */
    inBrief: React.ReactNode;
    /** For the cashflow-covered leader: which case chose it (cashflowCoveredGoal.ts). */
    goalNote?: string | null;
    /** Amber "Worth knowing" — a nil-residual annuity, a horizon warning. Null when nothing to say. */
    worthKnowing?: React.ReactNode;
    /** The same plan with the other kind of asset, and the gap — alternativeAssetNote.ts. */
    alternative?: AlternativeAssetNote | null;
    /** "Where the proceeds go" / "How it's funded" rows, with their heading. */
    moneyRows?: { heading: string; rows: Array<{ label: string; amount: number }> } | null;
    /** Anything else the module shows inside "What this strategy does" (annuity terms, a quote control). */
    children?: React.ReactNode;
    isSelected: boolean;
    onSelect: () => void;
    onExplain: () => void;
    onRisks: () => void;
    /** Generates the Statement of Advice for this plan — offered only once it is selected. */
    onGenerateSoa?: () => void;
}

/** The line under a tile: the baseline's figure and the change, coloured by whether the move is good. */
const Baseline = ({ value, baseline, word, upIsGood }: { value: number; baseline: number | undefined; word: string; upIsGood: boolean }) => {
    if (baseline == null) return null;
    const d = value - baseline;
    const flat = Math.round(d) === 0;
    const good = upIsGood ? d > 0 : d < 0;
    return (
        <p className="mt-1 text-[13px] tabular-nums text-slate-500">
            {word} {formatCurrency(baseline)} ·{' '}
            <span className={`font-semibold ${flat ? 'text-slate-400' : good ? 'text-emerald-700' : 'text-rose-700'}`}>
                {flat ? 'no change' : `${d > 0 ? '+' : '−'}${formatCurrency(Math.abs(d))}`}
            </span>
        </p>
    );
};

const Tile = ({ label, value, tone, children }: { label: string; value: string; tone: 'good' | 'bad' | 'neutral'; children?: React.ReactNode }) => (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <p className={`mt-1 text-[20px] font-bold leading-none tabular-nums ${tone === 'good' ? 'text-emerald-700' : tone === 'bad' ? 'text-rose-700' : 'text-slate-900'}`}>{value}</p>
        {children}
    </div>
);

const EngineStrategyCard = ({
    name, description, badges, belowBaseline, snapshot, baseline, baselineWord, legacyYears, flow, inBrief, goalNote = null,
    worthKnowing = null, alternative = null, moneyRows = null, children, isSelected, onSelect, onExplain, onRisks, onGenerateSoa,
}: EngineStrategyCardProps) => {
    const pension = Number(snapshot.pension) || 0;
    const cash = Number(snapshot.netCashflow) || 0;
    const estate = snapshot.legacyValue;
    return (
        <div className={`rounded-xl border border-l-4 p-4 shadow-sm transition-all ${isSelected ? 'border-blue-500 border-l-blue-600 bg-blue-50/40 ring-1 ring-blue-200' : belowBaseline ? 'border-slate-200 border-l-slate-400 bg-white' : 'border-slate-200 border-l-blue-500 bg-white'}`}>
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                {badges.map((b) => (
                    <Badge key={b} tone={belowBaseline ? 'neutral' : 'info'}>{b}</Badge>
                ))}
            </div>
            <h4 className="text-lg font-semibold text-gray-900">{name}</h4>
            {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
            {belowBaseline && (
                <p className="mt-1.5 rounded-lg border border-slate-200/70 border-l-4 border-l-amber-500 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700">{belowBaseline}</p>
            )}

            <PlainWordsCard eyebrow="What this strategy does" className="mt-3">
                {goalNote && (
                    <p className="mb-2.5 rounded-lg border-l-4 border-blue-400 bg-blue-50/60 py-2 pl-3 pr-2 text-[13px] leading-relaxed text-blue-900">{goalNote}</p>
                )}
                <MoneyFlow steps={flow.filter((s) => s.amount > 0)} />
                <p className="mt-2.5 text-[13px] leading-relaxed text-slate-700">{inBrief}</p>
                {worthKnowing && (
                    <div className="mt-2.5 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-slate-700">
                        <span className="font-semibold">Worth knowing.</span> {worthKnowing}
                    </div>
                )}
                {alternative && (
                    <p className="mt-2.5 rounded-lg border-l-4 border-blue-400 bg-blue-50/60 py-2 pl-3 pr-2 text-[13px] leading-relaxed text-blue-900">
                        <span className="font-semibold">{alternative.question}</span> {alternative.answer}
                    </p>
                )}
                {moneyRows && moneyRows.rows.length > 0 && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">{moneyRows.heading}</p>
                        <ul className="text-sm text-slate-700 space-y-0.5">
                            {moneyRows.rows.map((f, i) => (
                                <li key={i} className="flex justify-between"><span>{f.label}</span><span className="font-medium tabular-nums">{formatCurrency(f.amount)}</span></li>
                            ))}
                        </ul>
                    </div>
                )}
                {children}
            </PlainWordsCard>

            <div className="grid grid-cols-1 gap-3 mt-3 sm:grid-cols-3">
                <Tile label="Age Pension" value={formatCurrency(pension)} tone="neutral">
                    <Baseline value={pension} baseline={baseline?.pension} word={baselineWord} upIsGood />
                </Tile>
                <Tile label="Cashflow (year 1)" value={formatCurrency(cash)} tone={cash < 0 ? 'bad' : 'good'}>
                    <Baseline value={cash} baseline={baseline?.netCashflow} word={baselineWord} upIsGood />
                </Tile>
                <Tile label={`Estate (year ${legacyYears})`} value={estate != null ? formatCurrency(estate) : '—'} tone="neutral">
                    {estate != null && <Baseline value={estate} baseline={baseline?.legacyValue} word={baselineWord} upIsGood />}
                </Tile>
            </div>

            {/* Three buttons, not five (owner, 2 Oct 2026): understand it, see what could go wrong,
                choose it. The year-by-year deep dive lives inside "Explain this". */}
            <div className="mt-4 flex gap-3">
                <Button onClick={onExplain} variant="secondary" size="sm" icon={BookOpen} className="flex-1">Explain this</Button>
                <Button onClick={onRisks} variant="secondary" size="sm" icon={ShieldAlert} className="flex-1">What could go wrong</Button>
                <Button onClick={onSelect} variant="primary" size="sm" icon={CheckCircle} className="flex-1">{isSelected ? 'Selected ✓' : 'Select Strategy'}</Button>
            </div>
            {isSelected && (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-slate-200/70 border-l-4 border-l-emerald-500 bg-white px-3 py-2 text-sm text-emerald-900 shadow-sm">
                    <p className="min-w-0 flex-1">
                        <span className="font-semibold">This is your selected strategy.</span> It is saved with your file. To adjust the details, switch to Manual Control — your selection carries over.
                    </p>
                    {onGenerateSoa && (
                        <button
                            type="button"
                            onClick={onGenerateSoa}
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
                        >
                            Generate your Statement of Advice →
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default EngineStrategyCard;
